import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { debounceTime, distinctUntilChanged, merge, Subscription } from 'rxjs';
import { ModelService } from '../../../../services/vehicles/model-service';
import { Model } from '../../../../models/model';
import { BodyType, RentalType, UserRole } from '../../../../models/enums';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';
import { DeleteModelForm } from '../../../../@shared/forms/vehicles/models/delete-model-form/delete-model-form';
import { Employee } from '../../../../models/employee';
import { EmployeeService } from '../../../../services/employees/employee-service';
import { Params } from '../../../../services/utilis/add-params-service';
import { MultiSelect } from 'primeng/multiselect';

@Component({
  selector: 'app-models-list-component',
  imports: [CommonModule, ReactiveFormsModule, PaginatorModule, Select, MultiSelect, InputText, IconField, InputIcon, DeleteModelForm, RouterLink],
  templateUrl: './models-list-component.html',
  styleUrl: './models-list-component.scss'
})
export class ModelsListComponent implements OnInit, OnDestroy {

  @Output() totalRecordsChange = new EventEmitter<number>();

  models = signal<Model[]>([]);
  totalRecords = signal(0);
  first = 0;
  pageSize = 20;
  currentPage = 0;

  searchControl = new FormControl('');
  bodyTypeControl = new FormControl<string | null>(null);
  rentalTypeControl = new FormControl<string | null>(null);

  bodyTypeOptions = enumToOptions(BodyType);
  rentalTypeOptions = enumToOptions(RentalType);

  selectedModel: Model;
  deleteModelDialog = false;

  employee = signal<Employee | undefined>(undefined);
  UserRole = UserRole;

  get isAllActive(): boolean {
    return !this.bodyTypeControl.value && !this.rentalTypeControl.value;
  }

  private sub!: Subscription;
  private modelService = inject(ModelService);
  private employeeService = inject(EmployeeService);
  private router = inject(Router);

  ngOnInit() {
    this.employee = this.employeeService.employee;
    this.fetchModels();

    this.sub = merge(
      this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()),
      this.bodyTypeControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()),
      this.rentalTypeControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()),
    ).subscribe(() => {
      this.currentPage = 0;
      this.first = 0;
      this.fetchModels();
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  fetchModels() {
    const params: Params = {
      page: this.currentPage,
      size: this.pageSize,
      sortBy: 'updatedAt',
      sortDirection: 'DESC',
    };
    if (this.searchControl.value) params['search'] = this.searchControl.value;
    if (this.bodyTypeControl.value) params['bodyType'] = this.bodyTypeControl.value;
    if (this.rentalTypeControl.value) params['rentalType'] = this.rentalTypeControl.value;

    this.modelService.getModelsList(params).subscribe(res => {
      this.models.set(res.data.content);
      this.totalRecords.set(res.data.totalElements);
      this.totalRecordsChange.emit(res.data.totalElements);
      this.first = this.currentPage * this.pageSize;
    });
  }

  clearAll() {
    this.bodyTypeControl.setValue(null, { emitEvent: false });
    this.rentalTypeControl.setValue(null, { emitEvent: false });
    this.currentPage = 0;
    this.first = 0;
    this.fetchModels();
  }

  onPageChange(event: PaginatorState) {
    this.currentPage = event.page ?? 0;
    this.pageSize = event.rows ?? 20;
    this.first = event.first ?? 0;
    this.fetchModels();
  }

  updateModel(model: Model) {
    this.router.navigate([`model-form/${model.id}`]);
  }

  openDeleteModelDialog(model: Model) {
    this.selectedModel = model;
    this.deleteModelDialog = true;
  }

  onModelDeleted() {
    this.fetchModels();
    this.deleteModelDialog = false;
  }
}
